import { useState } from 'react';
import PropTypes from 'prop-types';

import { useNavigate } from 'react-router';

import { connect } from 'react-redux';

import { styled } from '@prose-ui/legacy';
import {
  useGetProductIngredientsListModal,
  useGetSkinCareProductIngredientsList,
} from 'hooks/useGetProductIngredients';

import IngredientsListModal from 'Components/IngredientsListModal';
import { Button } from 'Components/LegacyButton';
import ScaleLoader from 'Components/ScaleLoader';
import Spacer from 'Components/Spacer';
import { IngredientsDisclaimer } from '../Components/IngredientsDisclaimer';

import { SKINCARE_CART_URL } from 'constants/cart';
import { productsCategories } from 'constants/products';

import ErrorContainer from 'Containers/ErrorContainer';

import consultationCategoryProp from 'PropTypes/consultationProps';

import ConsultationLoading from '../Scenes/ConsultationLoading';
import IngredientList from '../Scenes/Posology/IngredientList';
import IngredientSection from '../Scenes/Posology/IngredientSection';
import QuestionLayout from '../Scenes/QuestionLayout';

import { skincareIngredients as content } from 'assets/content/consultation/consultation';

import { subscriptionCategoryWithMissingConsultation } from 'dux/subscriptions/selectors';
import { getUserFirstName } from 'dux/user/selectors';

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const SkincareIngredientsContainer = ({
  consultationCategory,
  name,
  subscriptionCategoryToRedirect,
}) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  // useGetProductIngredients for ingredients page in consultation
  const { error, fetchSkincareIngredients, ingredientsData, status } =
    useGetSkinCareProductIngredientsList(isOpen, productsCategories.SKINCARE);
  const { incisList, incisError, incisStatus } = useGetProductIngredientsListModal(
    isOpen,
    productsCategories.SKINCARE
  );
  if (['idle', 'loading'].includes(status)) {
    return <ConsultationLoading />;
  }

  if (status === 'error') {
    return (
      <ErrorContainer
        error={error}
        retry={fetchSkincareIngredients}
        sentryMessage="Invalid state on skincare ingredients"
      />
    );
  }

  /**
   * If the user was redirected to the consultation from an upsell subscription card
   * Redirect him to subscription from here
   * Otherwise, add preselected product to URL if needed
   */
  const nextUrl = subscriptionCategoryToRedirect
    ? `/account/subscription/${subscriptionCategoryToRedirect}`
    : SKINCARE_CART_URL;

  return (
    <QuestionLayout
      addFooter
      consultationCategory={consultationCategory}
      display="block"
      showHeaderBackground
      showProgressTracker={false}
      submit={() => navigate(nextUrl)}
      subtitle={content.subtitle}
      title={content.title}
      usePadding={false}
    >
      {ingredientsData ? (
        <>
          {ingredientsData.map((block, key) => (
            <div key={String(`${block?.section}-${key}`)}>
              <Spacer axis="vertical" size={36} />
              {block?.ingredients?.length > 0 && (
                <IngredientSection title={content.sectionLabel(name)}>
                  <IngredientList ingredients={block.ingredients} showIncludedIn />
                </IngredientSection>
              )}
            </div>
          ))}
          {incisList && (
            <IngredientsListModal
              hasError={Boolean(incisError)}
              isLoading={['loading', 'idle'].includes(incisStatus)}
              isOpen={isOpen}
              name={name}
              onClose={() => setIsOpen(false)}
              products={incisList}
            />
          )}
          <ButtonContainer>
            <Button onClick={() => setIsOpen(open => !open)} variant="noir">
              view full list of ingredients
            </Button>
          </ButtonContainer>
        </>
      ) : (
        <LoaderWrapper>
          <ScaleLoader />
        </LoaderWrapper>
      )}
      <IngredientsDisclaimer />
    </QuestionLayout>
  );
};

SkincareIngredientsContainer.propTypes = {
  consultationCategory: consultationCategoryProp.isRequired,
  name: PropTypes.string,
  subscriptionCategoryToRedirect: PropTypes.string,
};

SkincareIngredientsContainer.defaultProps = {
  name: null,
  subscriptionCategoryToRedirect: null,
};

export default connect(state => ({
  name: getUserFirstName(state),
  subscriptionCategoryToRedirect: subscriptionCategoryWithMissingConsultation(state),
}))(SkincareIngredientsContainer);
